import AlertMessage from '@components/alerts/AlertMessage';
import Button from '@components/button/Button';
import { ContactUs } from '@components/contactUsLink/ContactUs';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import { SurfaceContainer } from '@components/layout/surfaceContainer/SurfaceContainer';
import { BrandLogo } from '@components/logos/brandLogo/BrandLogo';
import { Form, Space, Typography } from 'antd';
import 'firebase/compat/auth';
import { isNil } from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { MdCheck, MdCheckBox, MdOutlinePassword } from 'react-icons/md';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { loginUser, logoutUser } from '../../actions';
import { FORECAST_API_URL } from '../../api/firebaseApi';

const { Title, Text } = Typography;

interface CreatePasswordProps {
  loginUserAction: (user: any) => void;
  logoutUserAction: () => void;
}

type EmailPasswordToken = {
  tokenCode: string;
  password: string;
};

const CreatePassword: React.FC<CreatePasswordProps> = ({
  loginUserAction,
  logoutUserAction,
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'dashboardPassword',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const tokenCodeFromUrlParams = searchParams.get('code');

  const [form] = Form.useForm();

  useEffect(() => {
    if (!tokenCodeFromUrlParams) {
      navigate('/');
    }
  }, [tokenCodeFromUrlParams, navigate]);

  const fetchUserEmail = async ({
    tokenCode,
    password,
  }: EmailPasswordToken) => {
    if (isNil(password)) {
      return {};
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: tokenCode,
        password,
      }),
    };

    setIsLoading(true);
    const response = await fetch(
      `${FORECAST_API_URL}/user/create-password`,
      requestOptions,
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error);
    }

    return { email: data?.userEmail, password };
  };

  const onSubmitNewPassword = async (values: any) => {
    try {
      const { email, password } = await fetchUserEmail({
        tokenCode: tokenCodeFromUrlParams!,
        password: values?.confirmPassword,
      });

      await logoutUserAction();
      await loginUserAction({ email, password });
      navigate('/');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getCheckedIcon = (isValid: boolean) =>
    isValid ? <MdCheckBox color="#5cb85c" size={16} /> : <MdCheck size={16} />;

  const password = Form.useWatch('password', form);

  const renderPasswordChecklist = useCallback(
    (passwordValue: string) => {
      const checks = [
        {
          valid: passwordValue?.length >= 8,
          label: t('containAtLeastLettersCheck'),
        },
        {
          valid: passwordValue && /[A-Z]/.test(passwordValue),
          label: t('containUppercaseCheck'),
        },
        {
          valid: passwordValue && /[a-z]/.test(passwordValue),
          label: t('containLowercaseCheck'),
        },
        {
          valid: /[0-9]/.test(passwordValue),
          label: t('containNumberCheck'),
        },
      ];

      return (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-2.5">
          <Text className="tw-text-[var(--color-text-weaker)]" strong>
            {t('yourPasswordMustBe')}
          </Text>
          <ul className="tw-px-0 tw-list-none">
            {checks.map((item: any, idx: number) => (
              <li key={`checked-item-${idx + 1}`}>
                <Text className="tw-text-[var(--color-text-weaker)] tw-flex tw-items-center">
                  {getCheckedIcon(item.valid)} {item.label}
                </Text>
              </li>
            ))}
          </ul>
        </div>
      );
    },
    [t],
  );

  return (
    <>
      <Helmet>
        <title>{t('settingPasswordTitle')}</title>
      </Helmet>
      <CenterPanel>
        <div>
          {error && (
            <AlertMessage className="tw-my-2" message={error} showIcon />
          )}
          <SurfaceContainer maxWidth={530} noBorder>
            <Space
              orientation="vertical"
              size="small"
              className="tw-p-[16px] tw-w-[530px]"
            >
              <BrandLogo
                size={56}
                wrapperClassName="tw-flex tw-justify-center"
              />

              <Title level={2} className="!tw-m-0 tw-text-center">
                {t('createPasswordTitle')}
              </Title>

              <Text className="tw-text-[--color-text-weaker] tw-flex tw-justify-center">
                {t('createPasswordSubHeading')}
              </Text>

              <Form
                form={form}
                layout="vertical"
                className="tw-w-full"
                onFinish={onSubmitNewPassword}
              >
                <InputWithFormItem
                  type="password"
                  inputName="password"
                  size="middle"
                  placeholderText={t('passwordPlaceholder')}
                  rules={[
                    () => ({
                      validator(_: any, value: string) {
                        if (!value || value?.length < 8) {
                          return Promise.reject(
                            new Error(t('atLeast8Characters')),
                          );
                        }
                        if (value.search(/[a-z]/) < 0) {
                          return Promise.reject(
                            new Error(t('atLeastLowercaseCharacters')),
                          );
                        }
                        if (value.search(/[A-Z]/) < 0) {
                          return Promise.reject(
                            new Error(t('atLeastUppercaseCharacters')),
                          );
                        }
                        if (value.search(/[0-9]/) < 0) {
                          return Promise.reject(
                            new Error(t('atLeastOneDigitCharacters')),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  PrefixIcon={MdOutlinePassword}
                  noStyle
                />

                {renderPasswordChecklist(password)}

                <InputWithFormItem
                  type="password"
                  inputName="confirmPassword"
                  size="middle"
                  placeholderText={t('confirmPasswordPlaceholder')}
                  rules={[
                    {
                      required: true,
                      message: t('confirmPassword'),
                    },
                    ({ getFieldValue }: any) => ({
                      validator(_: any, value: string) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('passwordMismatch')));
                      },
                    }),
                  ]}
                  PrefixIcon={MdOutlinePassword}
                />

                <Button
                  submit
                  size="middle"
                  type="primary"
                  label={t('updatePasswordButton')}
                  loading={isLoading}
                  fullWidth
                />
              </Form>
              <ContactUs wrapperClass="tw-flex tw-justify-center" />
            </Space>
          </SurfaceContainer>
        </div>
      </CenterPanel>
    </>
  );
};

export default connect(null, {
  loginUserAction: loginUser,
  logoutUserAction: logoutUser,
})(CreatePassword);
